import { createPersistedStore, storage } from '@connect-x-app/hybrid-utils';
import { FlightRoomUser, Message, MessageReceipt, MessageStatus } from '@connect-x-app/shared-domain-models';
import { createSerializableStorage, dateDiff, DateTimePartsEnum, getNow, sortMessagesByDate } from '@connect-x-app/shared-utils';

type MessagesMap = Map<string, Message>;
type ChannelMessageChunksMap = Map<string, MessagesMap[]>;

type MessageState = {
  addMessage: (payload: { addToMostRecentChunk?: boolean; message: Message }) => void;
  addMessages: (payload: { channelId: string; messages: Message[] }) => void;
  markMessagesReadByIds: (payload: { channelId: string; messageIds: string[]; readBy: FlightRoomUser }) => void;
  messagesMap: ChannelMessageChunksMap;
  removeMessage: (payload: { channelId: string; messageId: string }) => void;
  removeMessagesForChannels: (channelIds: string[]) => void;
  updateMessageStatus: (payload: { channelId: string; messageId: string; status: MessageStatus; userExternalId: string }) => void;
  updatePendingMessagesToFailed: () => void;
};

const sortChunks = (roomMessageChunks: MessagesMap[]): MessagesMap[] => {
  if (!roomMessageChunks || roomMessageChunks.length === 0) {
    return [];
  }

  const chunksWithSortedMessages = roomMessageChunks.map(chunk => {
    const sortedEntries = [...chunk.entries()].sort(([, msgA], [, msgB]) => sortMessagesByDate(msgA, msgB));
    return new Map(sortedEntries);
  });

  return chunksWithSortedMessages.sort((chunkA, chunkB) => {
    const firstMessageA = chunkA.values().next().value;
    const firstMessageB = chunkB.values().next().value;

    if (!firstMessageA || !firstMessageB) {
      return 0;
    }

    return sortMessagesByDate(firstMessageA, firstMessageB);
  });
};

// The API always returns messages with status SENT. Socket events update the store to
// DELIVERED/READ. This priority table prevents a background refetch from downgrading a
// socket-updated status back to SENT.
const STATUS_PRIORITY: Record<MessageStatus, number> = {
  [MessageStatus.FAILED]: -1,
  [MessageStatus.PENDING]: 0,
  [MessageStatus.SENT]: 1,
  [MessageStatus.DELIVERED]: 2,
  [MessageStatus.READ]: 3,
};

const getStatusPriority = (status: MessageStatus | null): number => (status !== null ? STATUS_PRIORITY[status] : -1);

const deriveStatusFromReceipts = (receipts: MessageReceipt[], fallback: MessageStatus | null): MessageStatus | null => {
  if (receipts.length === 0) {
    return fallback;
  }
  return receipts.reduce(
    (lowest, receipt) => (getStatusPriority(receipt.status) < getStatusPriority(lowest.status) ? receipt : lowest),
    receipts[0],
  ).status;
};

const addOrUpdateMessagesInChunks = (originalChunks: MessagesMap[], messages: Message[]): MessagesMap[] => {
  if (messages.length === 0) {
    return originalChunks;
  }

  const newServerIds = new Set(messages.map(m => m.id));
  const newClientIds = new Set(messages.map(m => m.externalId).filter((id): id is string => id != null));

  const chunksToMerge: MessagesMap[] = [];
  const unrelatedChunks: MessagesMap[] = [];

  for (const chunk of originalChunks) {
    const hasOverlap =
      [...chunk.keys()].some(key => newServerIds.has(key) || newClientIds.has(key)) ||
      [...chunk.values()].some(msg => newServerIds.has(msg.id));
    if (hasOverlap) {
      chunksToMerge.push(chunk);
    } else {
      unrelatedChunks.push(chunk);
    }
  }

  const existingById = new Map(chunksToMerge.flatMap(chunk => [...chunk.values()]).map(msg => [msg.id, msg]));

  const newMessagesMap = new Map(
    messages.map(message => {
      const incoming = { ...message, status: deriveStatusFromReceipts(message.receipts, message.status) };
      const existing = existingById.get(message.id);
      if (existing && getStatusPriority(existing.status) > getStatusPriority(incoming.status)) {
        return [message.id, { ...incoming, receipts: existing.receipts, status: existing.status }] as const;
      }
      return [message.id, incoming] as const;
    }),
  );

  if (chunksToMerge.length === 0) {
    return sortChunks([...unrelatedChunks, newMessagesMap]);
  }

  const existingEntries = chunksToMerge
    .flatMap(chunk => [...chunk.entries()])
    .filter(([key, msg]) => !newServerIds.has(key) && !newServerIds.has(msg.id) && !newClientIds.has(key));

  const mergedChunk = new Map([...existingEntries, ...newMessagesMap.entries()]);
  return sortChunks([...unrelatedChunks, mergedChunk]);
};

const applyReceiptToMessage = (message: Message, externalUserId: string, status: MessageStatus): Message => {
  const existingReceiptIndex = message.receipts.findIndex(r => r.user === externalUserId);

  const newReceipts = [...message.receipts];
  if (existingReceiptIndex !== -1) {
    newReceipts[existingReceiptIndex] = { ...newReceipts[existingReceiptIndex], status };
  } else {
    newReceipts.push({
      status,
      updatedAt: new Date().toISOString(),
      user: externalUserId,
    });
  }

  return {
    ...message,
    receipts: newReceipts,
    status: deriveStatusFromReceipts(newReceipts, status),
  };
};

const updateMessageInChunk = (
  chunk: MessagesMap,
  messageId: string,
  userExternalId: string,
  status: MessageStatus,
): { newChunk: MessagesMap; wasChanged: boolean } => {
  if (chunk.has(messageId)) {
    const message = chunk.get(messageId)!;
    const updatedMessage = applyReceiptToMessage(message, userExternalId, status);
    const newChunk = new Map(chunk);
    newChunk.set(messageId, updatedMessage);
    return { newChunk, wasChanged: true };
  }
  return { newChunk: chunk, wasChanged: false };
};

const updateMessagesInChunk = (
  chunk: MessagesMap,
  messageIds: Set<string>,
  status: MessageStatus,
  readBy: FlightRoomUser,
): { newChunk: MessagesMap; wasChanged: boolean } => {
  const idsInChunk = [...messageIds].filter(messageId => chunk.has(messageId));
  if (idsInChunk.length === 0) {
    return { newChunk: chunk, wasChanged: false };
  }

  const newChunk = new Map(chunk);
  idsInChunk.forEach(messageId => {
    if (newChunk.has(messageId)) {
      const message = newChunk.get(messageId)!;
      const updatedMessage = applyReceiptToMessage(message, readBy.externalId, status);
      newChunk.set(messageId, updatedMessage);
    }
  });

  return { newChunk, wasChanged: true };
};

const updatePendingMessagesInChunk = (chunk: MessagesMap): { newChunk: MessagesMap; wasAnythingUpdated: boolean } => {
  let chunkHasBeenModified = false;
  const newChunk = new Map(chunk);

  newChunk.forEach((message, messageId) => {
    if (message.status === MessageStatus.PENDING) {
      const diff = dateDiff(getNow(), message.createdAt, DateTimePartsEnum.MINUTE);

      if (diff < -1) {
        const updatedMessage = {
          ...message,
          status: MessageStatus.FAILED,
        };

        newChunk.set(messageId, updatedMessage);
        chunkHasBeenModified = true;
      }
    }
  });

  return { newChunk, wasAnythingUpdated: chunkHasBeenModified };
};

const updatePendingMessagesForChannel = (chunks: MessagesMap[]): { newChunks: MessagesMap[]; wasChanged: boolean } => {
  let wasChanged = false;

  const newChunks = chunks.map(chunk => {
    const { newChunk, wasAnythingUpdated: chunkWasUpdated } = updatePendingMessagesInChunk(chunk);
    if (chunkWasUpdated) {
      wasChanged = true;
    }
    return newChunk;
  });

  return { newChunks, wasChanged };
};

export const useMessageStore = createPersistedStore<MessageState>(
  (set, _) => ({
    addMessage: ({ addToMostRecentChunk, message }) =>
      set(state => {
        const channelId = message.channelId;
        if (!channelId) {
          return {};
        }

        const updatedMessages = new Map(state.messagesMap);
        const originalChunks = updatedMessages.get(channelId) ?? [];

        let isUpdated = false;

        const mappedChunks = originalChunks.map(chunk => {
          const clientMessageId = message.externalId;
          const serverId = message.id;

          if (clientMessageId && chunk.has(clientMessageId) && clientMessageId !== serverId) {
            const newChunk = new Map(chunk);
            if (serverId) {
              // Send-ack: reconcile the optimistic client id to the real server id.
              newChunk.delete(clientMessageId);
              newChunk.set(serverId, message);
            } else {
              // Optimistic re-add with no server id yet (e.g. retrying a failed send):
              // update in place under the client id instead of re-keying to an empty id.
              newChunk.set(clientMessageId, message);
            }
            isUpdated = true;
            return newChunk;
          }

          if (serverId && chunk.has(serverId)) {
            const newChunk = new Map(chunk);
            newChunk.set(serverId, message);
            isUpdated = true;
            return newChunk;
          }
          return chunk;
        });

        if (!isUpdated) {
          if (addToMostRecentChunk) {
            if (!mappedChunks.length) {
              mappedChunks.push(new Map());
            }
            mappedChunks[0].set(message.externalId ?? message.id, message);
          } else {
            mappedChunks.push(new Map([[message.externalId ?? message.id, message]]));
          }
        }

        const finalChunks = sortChunks(mappedChunks);
        updatedMessages.set(channelId, finalChunks);
        return { messagesMap: updatedMessages };
      }),

    addMessages: ({ channelId, messages }) =>
      set(state => {
        const updatedMessages = new Map(state.messagesMap);
        const originalChunks = updatedMessages.get(channelId) ?? [];

        const finalChunks = addOrUpdateMessagesInChunks(originalChunks, messages);

        if (finalChunks?.length) {
          updatedMessages.set(channelId, finalChunks);
        } else {
          updatedMessages.delete(channelId);
        }
        return { messagesMap: updatedMessages };
      }),

    markMessagesReadByIds: ({ channelId, messageIds, readBy }) =>
      set(state => {
        if (messageIds.length === 0) {
          return {};
        }
        const channelChunks = state.messagesMap.get(channelId);
        if (!channelChunks) {
          return {};
        }

        const idSet = new Set(messageIds);
        const updatedMessages = new Map(state.messagesMap);
        let wasChanged = false;

        const newChunks = channelChunks.map(chunk => {
          const { newChunk, wasChanged: chunkWasChanged } = updateMessagesInChunk(chunk, idSet, MessageStatus.READ, readBy);
          if (chunkWasChanged) {
            wasChanged = true;
          }
          return newChunk;
        });

        if (wasChanged) {
          updatedMessages.set(channelId, newChunks);
          return { messagesMap: updatedMessages };
        }

        return {};
      }),
    messagesMap: new Map(),
    removeMessage: ({ channelId, messageId }) =>
      set(state => {
        if (!state.messagesMap.has(channelId)) {
          return {};
        }

        const updatedMessages = new Map(state.messagesMap);
        const originalChunks = updatedMessages.get(channelId) ?? [];
        let wasMessageRemoved = false;

        const newChunks = originalChunks
          .map(chunk => {
            if (chunk.has(messageId)) {
              wasMessageRemoved = true;
              const updatedChunk = new Map(chunk);
              updatedChunk.delete(messageId);
              return updatedChunk;
            }
            return chunk;
          })
          .filter(chunk => chunk.size > 0);

        if (wasMessageRemoved) {
          updatedMessages.set(channelId, newChunks);
          return { messagesMap: updatedMessages };
        }

        return {};
      }),
    removeMessagesForChannels: (channelIds: string[]) =>
      set(state => {
        const newMessagesMap = new Map(state.messagesMap);
        let wasChanged = false;
        channelIds.forEach(id => {
          if (newMessagesMap.has(id)) {
            newMessagesMap.delete(id);
            wasChanged = true;
          }
        });

        return wasChanged ? { messagesMap: newMessagesMap } : {};
      }),

    updateMessageStatus: ({ channelId, messageId, status, userExternalId }) =>
      set(state => {
        const channelChunks = state.messagesMap.get(channelId);
        if (!channelChunks) {
          return {};
        }

        const updatedMessages = new Map(state.messagesMap);
        let wasChanged = false;

        const newChunks = channelChunks.map(chunk => {
          const { newChunk, wasChanged: chunkWasChanged } = updateMessageInChunk(chunk, messageId, userExternalId, status);
          if (chunkWasChanged) {
            wasChanged = true;
          }
          return newChunk;
        });

        if (wasChanged) {
          updatedMessages.set(channelId, newChunks);
          return { messagesMap: updatedMessages };
        }

        return {};
      }),
    updatePendingMessagesToFailed: () =>
      set(state => {
        const newMessagesMap = new Map(state.messagesMap);
        let wasAnythingUpdated = false;

        newMessagesMap.forEach((chunks, channelId) => {
          const { newChunks, wasChanged } = updatePendingMessagesForChannel(chunks);
          if (wasChanged) {
            newMessagesMap.set(channelId, newChunks);
            wasAnythingUpdated = true;
          }
        });

        return wasAnythingUpdated ? { messagesMap: newMessagesMap } : {};
      }),
  }),
  {
    name: 'messages-store',
    storage: createSerializableStorage(storage),
  },
);

const EMPTY_MESSAGE_CHUNKS: MessagesMap[] = [];

export const useMessageChunksForChannel = (channelId?: string): MessagesMap[] =>
  useMessageStore(state => (channelId ? state.messagesMap.get(channelId) : undefined)) ?? EMPTY_MESSAGE_CHUNKS;
